import * as XLSX from "xlsx"
import moment from "moment"
import { db } from "../../../utils/firebase"

const TYPE_LABELS = {
	INT_COMPENSATION: "INT/Compensation",
	EXT_PARTNER: "EXT/Partner",
	EXT_MARKETING: "EXT/Marketing",
	EXT_PURCHASED: "EXT/Purchased",
}

const STATUS_LABELS = {
	PENDING: "Not paid",
	PAID: "Paid & available",
	REDEEMED: "Redeemed",
}

// Column order for each tab (also written when a tab has no rows)
const VOUCHER_HEADERS = [
	"Reference",
	"Date issued",
	"Type",
	"Value (£)",
	"Status",
	"Issued by",
	"Bought by",
	"Bought by email",
	"Bought for",
	"Bought for email",
	"Note",
	"Booking reference",
	"Amount paid (£)",
	"PDF link",
	"Voucher ID",
]

const PAYMENT_HEADERS = ["Voucher reference", "Paid date", "Payee", "Payment reference", "Amount (£)"]

// Keep the number of in-flight Firestore reads bounded on large collections
const BATCH_SIZE = 50

const inBatches = async (items, fn) => {
	const results = []
	for (let i = 0; i < items.length; i += BATCH_SIZE) {
		results.push(...(await Promise.all(items.slice(i, i + BATCH_SIZE).map(fn))))
	}
	return results
}

// Fetch a set of documents from one collection, keyed by ID
const fetchDocsById = async (collection, ids) => {
	const docs = await inBatches([...new Set(ids.filter(Boolean))], (id) => db.doc(`${collection}/${id}`).get())
	return Object.fromEntries(docs.filter((doc) => doc.exists).map((doc) => [doc.id, doc.data()]))
}

const toDate = (timestamp) => (timestamp?.seconds ? timestamp.toDate() : null)

const fullName = (person) => [person?.first_name, person?.last_name].filter(Boolean).join(" ")

/**
 * Export the whole gift_vouchers collection, with its linked staff, clients,
 * bookings, PDFs and payments, into a two-tab XLSX workbook.
 *
 * @param {Object} usersMap Staff users keyed by UID (already loaded by the page)
 */
export const exportGiftVouchers = async (usersMap) => {
	// No orderBy, as Firestore would silently drop any voucher missing a purchased date
	const voucherDocs = await db.collection("gift_vouchers").get()
	const vouchers = voucherDocs.docs
		.map((doc) => ({ ...doc.data(), id: doc.id }))
		.sort((a, b) => (b.purchased?.seconds || 0) - (a.purchased?.seconds || 0))

	const [clients, bookings, pdfs, payments] = await Promise.all([
		fetchDocsById(
			"clients",
			vouchers.flatMap((voucher) => [voucher.purchased_by, voucher.purchased_for])
		),
		fetchDocsById(
			"bookings",
			vouchers.map((voucher) => voucher.bookingID)
		),
		inBatches(vouchers, async (voucher) => {
			if (!voucher.reference) return null
			const doc = await db.doc(`gift_vouchers/${voucher.id}/documents/${voucher.reference}`).get()
			return doc.exists ? doc.data().pdf_url : null
		}),
		inBatches(vouchers, async (voucher) => {
			const paymentDocs = await db.collection(`gift_vouchers/${voucher.id}/payments`).get()
			return paymentDocs.docs.map((doc) => doc.data())
		}),
	])

	const voucherRows = vouchers.map((voucher, index) => {
		const buyer = clients[voucher.purchased_by]
		const recipient = clients[voucher.purchased_for]
		const booking = bookings[voucher.bookingID]
		const amountPaid = payments[index].reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)

		return {
			Reference: voucher.reference || "",
			"Date issued": toDate(voucher.purchased),
			Type: TYPE_LABELS[voucher.type] || voucher.type || "",
			"Value (£)": Number(voucher.value) || 0,
			Status: STATUS_LABELS[voucher.status] || voucher.status || "",
			"Issued by": fullName(usersMap[voucher.issued_by]),
			"Bought by": fullName(buyer),
			"Bought by email": buyer?.email || "",
			"Bought for": fullName(recipient),
			"Bought for email": recipient?.email || "",
			Note: voucher.note || "",
			"Booking reference": booking?.reference ? `GHDB-${booking.reference}` : "",
			"Amount paid (£)": amountPaid,
			"PDF link": pdfs[index] || "",
			"Voucher ID": voucher.id,
		}
	})

	const paymentRows = vouchers.flatMap((voucher, index) =>
		payments[index]
			.map((payment) => ({
				"Voucher reference": voucher.reference || "",
				// Website payments only carry a created timestamp
				"Paid date": toDate(payment.paid_date) || toDate(payment.created),
				Payee: payment.payee || "",
				"Payment reference": payment.reference || "",
				"Amount (£)": Number(payment.amount) || 0,
			}))
			.sort((a, b) => (b["Paid date"] || 0) - (a["Paid date"] || 0))
	)

	const workbook = XLSX.utils.book_new()
	const addSheet = (name, rows, headers, dateFormat) => {
		const sheet = XLSX.utils.json_to_sheet(rows, { header: headers, dateNF: dateFormat })
		XLSX.utils.book_append_sheet(workbook, sheet, name)
	}

	addSheet("Vouchers", voucherRows, VOUCHER_HEADERS, "dd/mm/yyyy hh:mm")
	addSheet("Payments", paymentRows, PAYMENT_HEADERS, "dd/mm/yyyy")

	XLSX.writeFile(workbook, `gift-vouchers-${moment().format("YYYY-MM-DD")}.xlsx`)

	return { vouchers: voucherRows.length, payments: paymentRows.length }
}
